import React, { useEffect, useState, useContext } from 'react';
import { NavigationContext } from '../../context/NavigationContext';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import AddRechnungWizard from './projectDetails/AddRechnungWizard';
import RechnungenTable from './projectDetails/RechnungenTable';
import ProjectDetailsHeader from './projectDetails/ProjectDetailsHeader';
import './ProjectDetails.css';

interface Project {
    id: number;
    name: string;
    description: string | null;
    status: string;
    projectFiles?: ProjectFile[];
    rtdKontoId: number | null;
    rtdKonto?: RtdKonto;
}

interface RtdKonto {
    id: number;
    accountName: string;
    institute: string;
    iban: string;
    bic: string;
    glaeubigerId: string;
}

interface ProjectFile {
    id: number;
    filepath: string;
    projectId: number;
}

interface RechnungPosition {
    id: number;
    description: string;
    pricePerItem: number;
    numberOfItems: number;
    tax: number;
    projectFileUsed?: ProjectFile | null;
}

interface Rechnung {
    id: number;
    amount: number;
    date: string;
    projectId: number;
    pdfPath?: string | null;
    project?: {
        id: number;
        name: string;
        status: string;
    };
    roundTable?: {
        id: number;
        name: string;
        number: number;
    };
    rechnungPositions?: RechnungPosition[];
}

const ProjectDetailsComponent: React.FC = () => {
    const navigationContext = useContext(NavigationContext);
    const authContext = useContext(AuthContext);
    const [project, setProject] = useState<Project | null>(null);
    const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
    const [loading, setLoading] = useState(true);
    const [rechnungenLoading, setRechnungenLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddWizard, setShowAddWizard] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    if (!navigationContext) {
        throw new Error('ProjectDetailsComponent must be used within a NavigationProvider');
    }
    if (!authContext) {
        throw new Error('ProjectDetailsComponent must be used within an AuthProvider');
    }

    const { path, navigateTo } = navigationContext;
    const { token } = authContext;

    // Extract project ID from path (e.g., /projectdetails/1)
    const projectId = path.split('/projectdetails/')[1];

    useEffect(() => {
        const fetchProject = async () => {
            if (!projectId || isNaN(Number(projectId))) {
                setError('Ungültige Projekt-ID');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/projects/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Projekt nicht gefunden');
                    }
                    throw new Error('Fehler beim Laden des Projekts');
                }

                const data: Project = await response.json();
                setProject(data);
                setError(null);
            } catch (error) {
                console.error('Error fetching project:', error);
                setError(error instanceof Error ? error.message : 'Fehler beim Laden des Projekts');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden des Projekts');
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [projectId, token]);

    useEffect(() => {
        const fetchRechnungen = async () => {
            if (!projectId || isNaN(Number(projectId))) {
                setRechnungenLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/rechnungen/project/${projectId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden der Rechnungen');
                }

                const data: Rechnung[] = await response.json();
                setRechnungen(data);
            } catch (error) {
                console.error('Error fetching Rechnungen:', error);
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Rechnungen');
            } finally {
                setRechnungenLoading(false);
            }
        };

        if (!loading && project) {
            fetchRechnungen();
        }
    }, [projectId, token, loading, project]);

    const handleAddRechnungSuccess = async () => {
        setShowAddWizard(false);
        
        // Refresh the Rechnungen list
        try {
            const response = await fetch(`/api/rechnungen/project/${projectId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data: Rechnung[] = await response.json();
                setRechnungen(data);
            }
        } catch (error) {
            console.error('Error refreshing Rechnungen:', error);
        }
        
        // Trigger refresh of PDF status and other components
        setRefreshTrigger(prev => prev + 1);
    };

    const handleViewRechnungDetails = (rechnungId: number) => {
        navigateTo(`/rechnungdetails/${projectId}/${rechnungId}`);
    };

    if (loading) {
        return (
            <div className="project-details-container">
                <div className="project-details-loading">Lade Projektdetails...</div>
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="project-details-container">
                <div className="project-details-header">
                    <button className="back-button" onClick={() => navigateTo('/projects')} aria-label="Back to projects">
                        ←
                    </button>
                    <h1>Projektdetails</h1>
                </div>
                <div className="project-details-error">
                    <h2>Fehler</h2>
                    <p>{error || 'Projekt nicht gefunden'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="project-details-container">
            <div className="project-details-content">
                <ProjectDetailsHeader
                    project={project}
                    token={token!}
                    onProjectUpdate={setProject}
                    refreshTrigger={refreshTrigger}
                />

                <RechnungenTable
                    rechnungen={rechnungen}
                    loading={rechnungenLoading}
                    projectId={projectId}
                    onRechnungenUpdate={setRechnungen}
                    onAddClick={() => setShowAddWizard(true)}
                    onViewDetails={handleViewRechnungDetails}
                />
            </div>

            {/* Add Rechnung Wizard */}
            {showAddWizard && project && token && (
                <AddRechnungWizard
                    projectId={Number(projectId)}
                    projectName={project.name}
                    onClose={() => setShowAddWizard(false)}
                    onSuccess={handleAddRechnungSuccess}
                    token={token!}
                />
            )}
        </div>
    );
};

export default ProjectDetailsComponent;
